import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  User,
  MapPin,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  Phone,
  Mail,
  Home,
  Edit,
  Save,
  X,
  Image as ImageIcon,
  Download,
  Maximize2
} from 'lucide-react';
import { useAuth } from '@/store/hooks';
import { mobileClasses, isMobile } from '@/utils/mobileUtils';

interface Complaint {
  id: string;
  complaint_number: string;
  title: string;
  description: string;
  status: 'pending' | 'assigned' | 'in_progress' | 'on_hold' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  location?: string;
  created_at: string;
  updated_at: string;
  resolution_notes?: string;
  user?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  };
  category?: {
    id: string;
    name: string;
  };
  department?: {
    id: string;
    name: string;
  };
  assigned_to?: {
    id: string;
    first_name: string;
    last_name: string;
  };
}

interface ComplaintUpdate {
  id: string;
  complaint_id: string;
  update_type: 'status_change' | 'assignment' | 'comment' | 'escalation' | 'resolution' | 'attachment';
  description: string;
  old_status?: string;
  new_status?: string;
  created_at: string;
  updated_by?: {
    id: string;
    first_name: string;
    last_name: string;
    role: string;
  };
  updated_by_name?: string;
  complaint_user_member_id?: string;
  complaint_user_name?: string;
  is_internal: boolean;
}

interface FileAttachment {
  id: string;
  complaint_id: string;
  file_name: string;
  original_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  file_type: 'image' | 'pdf' | 'document' | 'spreadsheet' | 'archive' | 'text' | 'video' | 'audio' | 'other';
  uploaded_by: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  uploaded_by_name?: string;
  file_size_formatted?: string;
  file_icon?: string;
}

const ComplaintDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [complaint, setComplaint] = useState<Complaint | null>(null);
  const [updates, setUpdates] = useState<ComplaintUpdate[]>([]);
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showStatusEdit, setShowStatusEdit] = useState(false);
  const [newStatus, setNewStatus] = useState<string>('');
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchComplaintDetails();
      fetchComplaintUpdates();
      fetchAttachments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.role]);

  const fetchComplaintDetails = async () => {
    try {
      const response = await fetch(`/api/v1/complaints/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setComplaint(data.data);
        setNewStatus(data.data.status);
      } else {
        console.error('Failed to fetch complaint details');
        alert('Failed to load complaint details');
      }
    } catch (error) {
      console.error('Error fetching complaint details:', error);
      alert('Error loading complaint details');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchComplaintUpdates = async () => {
    try {
      const isAdmin = user?.role === 'universal_admin' || user?.role === 'office_admin' || user?.role === 'department_head';
      const includeInternal = isAdmin ? 'true' : 'false';
      const response = await fetch(`/api/v1/complaint-updates/complaint/${id}?include_internal=${includeInternal}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUpdates(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching complaint updates:', error);
    }
  };

  const fetchAttachments = async () => {
    try {
      const response = await fetch(`/api/v1/file-attachments/complaint/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAttachments(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching attachments:', error);
    }
  };

  const getAttachmentUrl = (attachment: FileAttachment): string => {
    // file_path is like "complaints/filename.jpg"
    // We need to construct the full URL: /uploads/complaints/filename.jpg
    if (attachment.file_path.startsWith('/')) {
      return attachment.file_path;
    }
    return `/uploads/${attachment.file_path}`;
  };

  const handleStatusUpdate = async () => {
    if (!complaint || newStatus === complaint.status) {
      setShowStatusEdit(false);
      return;
    }

    setIsUpdating(true);
    try {
      const response = await fetch(`/api/v1/complaints/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        const data = await response.json();
        setComplaint(data.data);
        setShowStatusEdit(false);
        // Refresh updates to show the status change
        fetchComplaintUpdates();
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating complaint status');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setIsSubmittingComment(true);
    try {
      const response = await fetch('/api/v1/complaint-updates', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          complaint_id: id,
          update_type: 'comment',
          description: newComment.trim(),
          is_internal: false
        })
      });

      if (response.ok) {
        setNewComment('');
        fetchComplaintUpdates();
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to add comment');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Error adding comment');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved':
      case 'closed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'assigned':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'on_hold':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'pending':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUpdateTypeLabel = (type: string) => {
    switch (type) {
      case 'status_change':
        return 'Status Changed';
      case 'assignment':
        return 'Assignment';
      case 'comment':
        return 'Comment';
      case 'escalation':
        return 'Escalation';
      case 'resolution':
        return 'Resolution';
      case 'attachment':
        return 'Attachment';
      default:
        return type;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading complaint details...</p>
        </div>
      </div>
    );
  }

  if (!complaint) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Complaint Not Found</h2>
          <p className="text-gray-600 mb-4">The complaint you're looking for doesn't exist.</p>
          <Link
            to="/app/complaints"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Complaints
          </Link>
        </div>
      </div>
    );
  }

  const isAdmin = user?.role === 'universal_admin' || user?.role === 'office_admin' || user?.role === 'department_head';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className={`max-w-7xl mx-auto ${mobileClasses.container} py-6 sm:py-8`}>
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className={`${mobileClasses.flexMobile} items-center mb-4`}>
            <button
              onClick={() => navigate('/app/complaints')}
              className={`${mobileClasses.flexMobile} items-center text-gray-600 hover:text-gray-900 ${isMobile() ? 'mb-2' : 'mr-4'}`}
            >
              <ArrowLeft className={`${isMobile() ? 'h-4 w-4' : 'h-5 w-5'} ${isMobile() ? 'mr-1' : 'mr-2'}`} />
              <span className={isMobile() ? 'text-sm' : ''}>Back to Complaints</span>
            </button>
          </div>
          <div>
            <h1 className={`${mobileClasses.textMobileXLarge} font-bold text-gray-900`}>
              Complaint #{complaint.complaint_number}
            </h1>
            <p className={`mt-1 ${mobileClasses.textMobile} text-gray-600`}>
              {complaint.title}
            </p>
          </div>
        </div>

        {/* Top Three Sections */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* User Information */}
          {complaint.user && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <User className="h-5 w-5 mr-2" />
                Complainant
              </h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {complaint.user.first_name} {complaint.user.last_name}
                  </p>
                </div>
                {complaint.user.email && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Mail className="h-4 w-4 mr-2" />
                    {complaint.user.email}
                  </div>
                )}
                {complaint.user.phone && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone className="h-4 w-4 mr-2" />
                    {complaint.user.phone}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Category & Department */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Category & Department</h2>
            <div className="space-y-3">
              {complaint.category && (
                <div>
                  <p className="text-xs font-medium text-gray-500">Category</p>
                  <p className="text-sm text-gray-900">{complaint.category.name}</p>
                </div>
              )}
              {complaint.department && (
                <div>
                  <p className="text-xs font-medium text-gray-500">Department</p>
                  <p className="text-sm text-gray-900">{complaint.department.name}</p>
                </div>
              )}
            </div>
          </div>

          {/* Assigned To */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Assignment</h2>
            <div className="space-y-3">
              {complaint.assigned_to ? (
                <div>
                  <p className="text-xs font-medium text-gray-500">Assigned To</p>
                  <p className="text-sm text-gray-900">
                    {complaint.assigned_to.first_name} {complaint.assigned_to.last_name}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-500">Not assigned</p>
              )}
              <div>
                <p className="text-xs font-medium text-gray-500">Status</p>
                <span className={`mt-1 inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(complaint.status)}`}>
                  {complaint.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">Priority</p>
                <span className={`mt-1 inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(complaint.priority)}`}>
                  {complaint.priority.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Complaint Details Card */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Complaint Details</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Description</h3>
              <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{complaint.description}</p>
            </div>

            {/* Attachments Section */}
            {attachments.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center">
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Attachments ({attachments.length})
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {attachments.map((attachment) => {
                    const attachmentUrl = getAttachmentUrl(attachment);
                    const isImage = attachment.file_type === 'image';
                    
                    return (
                      <div key={attachment.id} className="relative group border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                        {isImage ? (
                          <div className="relative aspect-square">
                            <img
                              src={attachmentUrl}
                              alt={attachment.original_name}
                              className="w-full h-full object-cover cursor-pointer"
                              onClick={() => setSelectedImage(attachmentUrl)}
                              onError={(e) => {
                                console.error('Error loading image:', attachmentUrl);
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                              <button
                                onClick={() => setSelectedImage(attachmentUrl)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-2 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100"
                                title="View full size"
                              >
                                <Maximize2 className="h-4 w-4 text-gray-800" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="aspect-square flex flex-col items-center justify-center p-4">
                            <div className="text-4xl mb-2">{attachment.file_icon || '📎'}</div>
                            <p className="text-xs text-gray-600 text-center truncate w-full px-2" title={attachment.original_name}>
                              {attachment.original_name}
                            </p>
                            <a
                              href={attachmentUrl}
                              download={attachment.original_name}
                              className="mt-2 inline-flex items-center px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              <Download className="h-3 w-3 mr-1" />
                              Download
                            </a>
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs p-1 px-2 truncate">
                          {attachment.original_name}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {complaint.location && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Location
                </h3>
                <p className="mt-1 text-sm text-gray-900">{complaint.location}</p>
              </div>
            )}

            {isAdmin && !showStatusEdit && (
              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowStatusEdit(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Update Status
                </button>
              </div>
            )}

            {showStatusEdit && (
              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Status</h3>
                <div className="flex items-center space-x-2">
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="assigned">Assigned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="on_hold">On Hold</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                  <button
                    onClick={handleStatusUpdate}
                    disabled={isUpdating}
                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      setShowStatusEdit(false);
                      setNewStatus(complaint.status);
                    }}
                    className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {complaint.resolution_notes && (
              <div>
                <h3 className="text-sm font-medium text-gray-500">Resolution Notes</h3>
                <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{complaint.resolution_notes}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div>
                <h3 className="text-sm font-medium text-gray-500 flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  Created
                </h3>
                <p className="mt-1 text-sm text-gray-900">{formatDate(complaint.created_at)}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  Last Updated
                </h3>
                <p className="mt-1 text-sm text-gray-900">{formatDate(complaint.updated_at)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Updates/Logs Section - Full Width */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <MessageSquare className="h-5 w-5 mr-2" />
            Updates & Logs
          </h2>

          {/* Add Comment */}
          <div className="mb-6 pb-6 border-b border-gray-200">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment or update..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleAddComment}
              disabled={!newComment.trim() || isSubmittingComment}
              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmittingComment ? 'Adding...' : 'Add Comment'}
            </button>
          </div>

          {/* Updates Table */}
          <div className="overflow-x-auto">
            {updates.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No updates yet</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Update
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Updated By
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Member Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Member ID
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {updates.map((update) => (
                    <tr key={update.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(update.created_at)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
                            {getUpdateTypeLabel(update.update_type)}
                          </span>
                          {update.old_status && update.new_status && (
                            <span className="text-xs text-gray-500 mr-2">
                              {update.old_status} → {update.new_status}
                            </span>
                          )}
                          <span className="text-gray-900">{update.description}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {update.updated_by_name || (update.updated_by ? `${update.updated_by.first_name} ${update.updated_by.last_name}` : 'N/A')}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {update.complaint_user_name || complaint?.user ? `${complaint.user.first_name} ${complaint.user.last_name}` : 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {update.complaint_user_member_id || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-7xl max-h-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 z-10 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-2 transition-all"
            >
              <X className="h-6 w-6 text-gray-800" />
            </button>
            <img
              src={selectedImage}
              alt="Full size attachment"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplaintDetailPage;
